import { ReactElement, useState, useEffect, memo } from 'react';
import Subscribe from '@/shared/Subscribe/ui/Subscribe';
import { useMedia } from '@/common/service/hooks/useMedia';
import Layout from '@/shared/Layout/ui/Layout';
import { IMeta } from '@/common/domain/Meta.domain';
import { IItemDestination } from '../domain/CommonPage.domain';
import { getCommonPageTitle } from '../app/pageUtils';
import { CommonPageMeta } from './commonPage/CommonPageMeta';
import { Description } from './commonPage/Description/Description';
import { Container } from '@/common/ui/containers/Container';
import * as S from './CommonPage.styles';
import { List } from './commonPage/List/List';

export type CommonPageProps = {
    destinations: IItemDestination[];
    noLinkToDestination?: boolean;
    upgradableList?: boolean;
    meta?: IMeta;
    type: 'blog' | 'partners';
};

const CommonPageContainer = ({
    type,
    destinations,
    noLinkToDestination,
    upgradableList,
}: CommonPageProps): ReactElement => {
    const isMobileView = useMedia('(max-width: 991px)');
    const [indexItemOpen, setIndexItemOpen] = useState<number | undefined>(0);
    const [currentType, setCurrentType] = useState<'blog' | 'partners'>(type);
    const pageTitle = `TripShock! ${getCommonPageTitle(type)}`;

    const openItem = (index: number, el: HTMLElement) => {
        if (isMobileView) {
            setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
        }

        if (indexItemOpen === index && isMobileView) {
            setIndexItemOpen(undefined);
            return;
        }

        setIndexItemOpen(index);
    };

    useEffect(() => {
        if (currentType !== type) {
            setIndexItemOpen(0);
            setCurrentType(type);
        }
    }, [type, currentType]);

    return (
        <Layout>
            <Container>
                <CommonPageMeta type={type} title={pageTitle} />
                <>
                    <S.ParagraphWrapper>
                        <S.CommonTitle data-test-id="Title">{pageTitle}</S.CommonTitle>
                        <Description type={type} title={pageTitle} />
                    </S.ParagraphWrapper>
                    <S.CommonWrapper data-test-id="Common_wrapper">
                        <List
                            destinations={destinations}
                            indexItemOpen={indexItemOpen}
                            openItem={openItem}
                            type={type}
                            selectedItem={destinations[indexItemOpen || 0] || destinations[0]}
                            noViewMore={noLinkToDestination}
                            upgradableList={upgradableList}
                        />
                    </S.CommonWrapper>
                </>
            </Container>
            <S.WrapperSubscribe>
                <Subscribe />
            </S.WrapperSubscribe>
        </Layout>
    );
};

export const CommonPage = memo(CommonPageContainer);
